// PRE RMA — Module Causes de décès CIM-10 (Tableau 20).
// Source : SAISIE_TAB20_CAUSES_DECES.html. Chaque « Ajouter » crée UN événement (cause, âge, sexe, nombre) :
// la grille du tableau est la somme des événements — saisie additive sans conflit hors-ligne.

export const AGES_DECES = ['0-28j', '29-59j', '2-11m', '1-4 ans', '5-14 ans', '15-24 ans', '25-59 ans', '60 ans et +'] as const;
export type SexeDeces = 'M' | 'F';

export const CAUSES_DECES: Array<{ code: string; libelle: string }> = [
  { code: 'A00-B99', libelle: 'Maladies infectieuses et parasitaires' }, { code: 'B54', libelle: 'Paludisme' },
  { code: 'C00-D48', libelle: 'Tumeurs' }, { code: 'D50-D89', libelle: 'Maladies de sang' },
  { code: 'E00-E90', libelle: 'Maladies endocriniennes' }, { code: 'F00-F99', libelle: 'Troubles mentaux' },
  { code: 'G00-G99', libelle: 'Maladies de système nerveux' }, { code: 'H00-H59', libelle: "Maladies de l'œil" },
  { code: 'H60-H95', libelle: "Maladies de l'oreille" }, { code: 'I00-I99', libelle: "Maladies de l'appareil circulatoire" },
  { code: 'J00-J99', libelle: "Maladies de l'appareil respiratoire" }, { code: 'K00-K93', libelle: "Maladies de l'appareil digestif" },
  { code: 'L00-L99', libelle: 'Maladies de la peau' }, { code: 'M00-M99', libelle: "Maladies de l'appareil ostéoarticulaire, muscles" },
  { code: 'N00-N99', libelle: "Maladies de l'appareil génito-urinaire" }, { code: 'O00-O99', libelle: 'Grossesse, accouchement' },
  { code: 'P00-P96', libelle: 'Affections période périnatale' }, { code: 'Q00-Q99', libelle: 'Malformations congénitales' },
  { code: 'R00-R99', libelle: 'Signes anormaux des examens cliniques' }, { code: 'S00-T98', libelle: 'Lésions traumatiques' },
  { code: 'V01-Y98', libelle: 'Causes externes' },
];

export interface EvenementDeces { cause: string; age: number; sexe: SexeDeces; nombre: number }

export function validerEvenementDeces(e: Partial<EvenementDeces>): { ok: boolean; erreurs: string[] } {
  const erreurs: string[] = [];
  if (!CAUSES_DECES.some((c) => c.code === e.cause)) erreurs.push('Cause CIM-10 inconnue.');
  if (!Number.isInteger(e.age) || (e.age as number) < 0 || (e.age as number) >= AGES_DECES.length) erreurs.push("Tranche d'âge invalide.");
  if (e.sexe !== 'M' && e.sexe !== 'F') erreurs.push('Sexe invalide (M ou F).');
  if (!Number.isInteger(e.nombre) || (e.nombre as number) < 1 || (e.nombre as number) > 999) erreurs.push('Nombre : entier entre 1 et 999.');
  return { ok: erreurs.length === 0, erreurs };
}

/** Index de colonne dans la grille (16 colonnes : âge × [M,F]). */
export const colonneDeces = (age: number, sexe: SexeDeces) => age * 2 + (sexe === 'F' ? 1 : 0);

export interface GrilleDeces {
  cellules: Record<string, number[]>; // code CIM-10 -> 16 valeurs
  totauxLigne: Record<string, number>;
  totauxColonne: number[];
  total: number; totalM: number; totalF: number;
}

export function agregerDeces(evts: EvenementDeces[]): GrilleDeces {
  const cellules: Record<string, number[]> = Object.fromEntries(CAUSES_DECES.map((c) => [c.code, Array(16).fill(0)]));
  for (const e of evts) { if (cellules[e.cause]) cellules[e.cause][colonneDeces(e.age, e.sexe)] += e.nombre; }
  const totauxLigne: Record<string, number> = {};
  const totauxColonne: number[] = Array(16).fill(0);
  for (const [code, v] of Object.entries(cellules)) { totauxLigne[code] = v.reduce((a, b) => a + b, 0); v.forEach((x, i) => { totauxColonne[i] += x; }); }
  const total = totauxColonne.reduce((a, b) => a + b, 0);
  const totalM = totauxColonne.filter((_, i) => i % 2 === 0).reduce((a, b) => a + b, 0);
  return { cellules, totauxLigne, totauxColonne, total, totalM, totalF: total - totalM };
}

/** Valeurs inhabituelles (alertes, non bloquantes) — mêmes règles que le fichier de saisie. */
export function anomaliesDeces(g: GrilleDeces): string[] {
  const a: string[] = [];
  const o = g.cellules['O00-O99'] ?? [];
  const p = g.cellules['P00-P96'] ?? [];
  o.forEach((v, k) => {
    if (v <= 0) return;
    const age = AGES_DECES[k >> 1], sx = k % 2 ? 'F' : 'M';
    if (sx === 'M') a.push(`O00-O99 (${age}, M) : grossesse/accouchement saisi chez un homme`);
    else if (k < 8) a.push(`O00-O99 (${age}, F) : grossesse/accouchement avant 5 ans`);
  });
  p.forEach((v, k) => { if (k >= 6 && v > 0) a.push(`P00-P96 (${AGES_DECES[k >> 1]}, ${k % 2 ? 'F' : 'M'}) : affection périnatale à un âge inhabituel`); });
  return a;
}

const norm = (s: string) => s.replace(/œ/gi, 'oe').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
/** Recherche prédictive : code ou mots du libellé, sans accents ni casse. Les codes commençant par la requête passent en premier. */
export function rechercherCause(q: string, max = 8): Array<{ code: string; libelle: string }> {
  const t = norm(q.trim());
  if (!t) return CAUSES_DECES.slice(0, max);
  const mots = t.split(/\s+/);
  const scored = CAUSES_DECES.map((c) => {
    const code = norm(c.code), lib = norm(c.libelle);
    const ok = mots.every((m) => code.includes(m) || lib.includes(m));
    return { c, ok, rang: code.startsWith(t) ? 0 : lib.startsWith(t) ? 1 : 2 };
  }).filter((x) => x.ok);
  return scored.sort((x, y) => x.rang - y.rang).slice(0, max).map((x) => x.c);
}
